import { ZoningCode } from 'src/app/modules/instance/models';
import { map, takeUntil } from 'rxjs/operators';
import { Observable, of, Subject } from 'rxjs';
import IStatisticByStatus = ZoningAPI.IStatisticByStatus;
import { Router } from '@angular/router';
import { ZoningCodeVersionService } from 'src/app/modules/instance/services';
import { ITaskModel } from '../../../../../shared/interfaces/task-model.interface';
import { Component, Input, OnChanges, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { getPrettyPropertyRecordCalculationStatus } from 'src/app/shared';
import * as _ from 'lodash';
import { ProcessCalculationStatisticService } from '../../../services/process-calculation-statistic.service';
import {
    getIntermediateStatisticIteration,
    ProcessCalculationStatistic,
    ProcessCalculationStatisticFactory
} from '../../../../../shared/models/process-calculation-statistic';
import * as moment from 'moment';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { SystemEventService } from '../../../services/system-event.service';
import { SystemEventParentType } from 'src/app/shared/enums/system-event-parent-type';
import { IProcessEventMetadata, ISystemEvent } from 'src/app/shared/interfaces/system-event.interface';
import { EventsStatComponentComponent } from '../../processes/sections/events-stat-component/events-stat-component.component';
import { getPrettyZCStatus } from 'src/app/shared/enums/zoning-code-status';

@Component({
    standalone: false,
    selector: 'app-task-process-detail-view',
    templateUrl: './task-process-detail-view.component.html',
    styleUrls: ['./task-process-detail-view.component.scss']
})
export class TaskCalcProcessDetailViewComponent implements OnInit, OnChanges, OnDestroy {
    @ViewChild('tabs') tabs;

    @ViewChild(EventsStatComponentComponent) eventsStatistic: EventsStatComponentComponent;

    private destroyed$ = new Subject();

    @Input()
    task: ITaskModel;

    @Input()
    zoningCode: ZoningCode;

    @Input()
    isZoningCodeLoading: boolean;

    totalRatios: number[] = [];
    totalProperties: string[] = [];
    ratiosByStatuses: number[] = [];
    propertiesByStatuses: string[] = [];
    processCalculationStatistics: ProcessCalculationStatistic[] = [];
    eventsList: ISystemEvent<IProcessEventMetadata>[] = [];

    selectedTabIndex = 0;

    systemEventParentType = SystemEventParentType;

    getPrettyZCStatus = getPrettyZCStatus;

    constructor(
        private readonly zcVersionService: ZoningCodeVersionService,
        private readonly router: Router,
        private readonly pcsService: ProcessCalculationStatisticService,
        private readonly systemEventService: SystemEventService
    ) {}

    get hasStatistic(): boolean {
        return !_.isEmpty(this.processCalculationStatistics);
    }

    get isMonitoringTabActive(): boolean {
        return this.selectedTabIndex === 1;
    }

    ngOnInit(): void {
        this.zcVersionService.newRecalculateZCVersionProcess.pipe(takeUntil(this.destroyed$)).subscribe(taskId => {
            this.router.navigate([`/instances/task/${taskId}`], { queryParams: { state_env: this.task.state } });
        });
        this.prepareTaskData();
        this.getProcessCalculationStatistics().subscribe();
        this.getSystemEvents().subscribe();
    }

    ngOnChanges(): void {
        this.prepareTaskData();
    }

    prepareTaskData(): void {
        if (this.task.isIndexing) {
            return;
        }
        // this.summaryStatistic({
        //     statsByStatus: this.task.statsByStatus,
        //     zoningCodeRecords: this.task.zoningCodeRecords
        // });
        this.prepareDataForChart();
    }

    prepareDataForChart(): void {
        this.totalRatios = [];
        this.totalProperties = [];
        this.ratiosByStatuses = [];
        this.propertiesByStatuses = [];

        const statsByStatus = Array.isArray(this.task?.statsByStatus) ? _.cloneDeep(this.task.statsByStatus) : [];

        statsByStatus.forEach(i => {
            this.fillDataForChart(i, this.totalRatios, this.totalProperties);
            this.fillDataForChart(i, this.ratiosByStatuses, this.propertiesByStatuses);
        });

        const summ = this.ratiosByStatuses.reduce((acc, cur: number) => {
            return acc + cur;
        }, 0);

        const uncalculated = Number((100 - summ).toFixed(2));
        if (uncalculated > 0) {
            this.ratiosByStatuses.push(uncalculated);
            this.propertiesByStatuses.push('Uncalculated properties');
        }
    }

    fillDataForChart(data: IStatisticByStatus, ratios: number[], properties: string[]) {
        if (!data || typeof data.ratio !== 'number' || !Number.isFinite(data.ratio)) {
            return;
        }
        ratios.push(data.ratio);
        properties.push(getPrettyPropertyRecordCalculationStatus(data.status));
    }

    statusFormatter(calculationStatusCode: number): string {
        return getPrettyPropertyRecordCalculationStatus(calculationStatusCode);
    }

    deleteProcess(isDeleteProcess: boolean): void {
        if (isDeleteProcess) {
            this.router.navigate([`/instances/tasks`]);
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next(undefined);
        this.destroyed$.complete();
    }

    getProcessCalculationStatistics(): Observable<void> {
        if (!this.task) {
            return of();
        }

        return this.pcsService.getProcessCalculationStatistics(this.task.state.toLocaleLowerCase(), this.task.id).pipe(
            map((data: ProcessCalculationStatistic[]) => {
                this.processCalculationStatistics = this.insertMissedIntervals(data);
            })
        );
    }

    getSystemEvents(): Observable<void> {
        if (!this.task) {
            return of();
        }

        return this.systemEventService
            .getSystemEvents(this.task.state.toLocaleLowerCase(), this.task.id, SystemEventParentType.PROCESS)
            .pipe(
                map((data: ISystemEvent<IProcessEventMetadata>[]) => {
                    this.eventsList = data;
                })
            );
    }

    insertMissedIntervals(data: ProcessCalculationStatistic[]) {
        if (_.isEmpty(data)) {
            return data;
        }

        const fieldsToInherit = [
            'TotalCRs',
            'AVGCRsPerHour',
            'AVGGISCalcTimeMs',
            'AVGDataLoadTimeMs',
            'AVGTransferTimeMs',
            'AVGSaveToDBTimeMs',
            'ThreadsCount',
            'BunchSize',
            'TotalPRsInProgress'
        ];

        try {
            for (let i = 0; i <= data.length - 1; i++) {
                const currentIteration = data[i];
                const nextIteration = data[i + 1];
                if (!currentIteration || !nextIteration) {
                    continue;
                }
                const current = currentIteration.createdAtM;
                const next = nextIteration.createdAtM;
                const difference = moment.duration(next.diff(current)).asMinutes();
                if (difference > 2) {
                    const intermediate1 = ProcessCalculationStatisticFactory(
                        getIntermediateStatisticIteration(currentIteration, fieldsToInherit)
                    );
                    intermediate1.setCreatedAt(currentIteration.createdAtM.clone().add(1, 'minutes'));
                    if (
                        intermediate1.createdAtM.isBefore(nextIteration.createdAtM) &&
                        !intermediate1.isEqualNumericValues(currentIteration)
                    ) {
                        data.splice(i + 1, 0, intermediate1);
                        i++;
                    }

                    const intermediate2 = ProcessCalculationStatisticFactory(
                        getIntermediateStatisticIteration(nextIteration, fieldsToInherit)
                    );
                    intermediate2.setCreatedAt(nextIteration.createdAtM.clone().add(-1, 'minutes'));
                    if (
                        intermediate2.createdAtM.isAfter(intermediate1.createdAtM) &&
                        !intermediate2.isEqualNumericValues(nextIteration)
                    ) {
                        data.splice(i + 1, 0, intermediate2);
                        i++;
                    }
                }
            }
        } catch (err) {
            console.log('TaskCalcProcessDetailViewComponent.insertMissedIntervals => ERROR:');
            console.log(err);
        }

        return data;
    }

    updateDataHandler(data: ProcessCalculationStatistic[]) {
        if (_.isEmpty(data)) {
            return;
        }
        this.processCalculationStatistics = this.insertMissedIntervals(data);
    }

    tabChanged(tabChangeEvent: MatTabChangeEvent): void {
        this.selectedTabIndex = tabChangeEvent.index;
    }

    updateEventsList(data: ISystemEvent<IProcessEventMetadata>[]) {
        if (_.isEmpty(data)) {
            return;
        }
        this.eventsList = data;
        if (this.eventsStatistic) {
            this.eventsStatistic.runUpdate();
        }
    }
}
